import {I18n} from '@aws-amplify/core';
import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';

import {formatTimestamp} from '../../../utils/TableTimestamps';
import {ReportRun} from '../types';
import {StatusBadge} from '../profile/StatusBadge';

// FNP-918: was `tables/columns.tsx` — a bare array where EVERY column was
// `render`-only, with no `accessor` on any of them.
//
// That is the distinction that made this module unsortable. `render` controls
// how a cell LOOKS; `accessor` is what sorting sorts BY. A render-only config
// looks complete in review and makes sorting structurally impossible. Moved
// into a hook to match the platform shape (checklist §3z).
//
// ⚠️ `created` and `succeededAt` are UNIX EPOCH SECONDS (typed `number`), not
// AWSDateTime strings. The old config cast them with
// `item.created as unknown as string` and handed the raw SECONDS to
// formatTimestamp, which moment parsed as MILLISECONDS — so every report run
// rendered as a date in January 1970. The cast silenced the very type error
// that was pointing at the bug. Conversion now happens in useReportRuns, so
// display and sort share one normalised value.
export const useReportRunsTableColumns = () => {

    const columns: DataTableColumn<ReportRun>[] = [
        {
            accessor: 'reportType',
            sortable: true,
            title: I18n.get('Report Type'),
            width: '1fr',
            render: (item: ReportRun) => (
                <NavLink to={`/report-runs/${encodeURIComponent(item.id)}`}>
                    {item.reportType}
                </NavLink>
            ),
        },
        {
            accessor: 'status',
            sortable: true,
            title: I18n.get('Status'),
            width: '1fr',
            // Enum field renders as a badge, not a raw string (§13). The badge
            // carries TEXT as well as colour, so colour is never the only
            // carrier of meaning.
            render: (item: ReportRun) => <StatusBadge record={item}/>,
        },
        {
            accessor: 'createdAtMillis',
            sortable: true,
            title: I18n.get('Created'),
            width: '1fr',
            render: (item: ReportRun) => formatTimestamp(item.createdAtMillis),
        },
        {
            accessor: 'succeededAtMillis',
            sortable: true,
            title: I18n.get('Succeeded At'),
            width: '1fr',
            // A run that has not succeeded has no succeededAt — that is a
            // STATE, not missing data, so it renders the em-dash placeholder
            // rather than the empty string the old column produced (§13), and
            // sorts LAST in both directions.
            render: (item: ReportRun) => formatTimestamp(item.succeededAtMillis),
        },
    ];

    return {columns};
};
